import { Link } from 'react-router-dom';
import { Footer } from '../components/layout/Footer';

const LAST_UPDATED = 'March 14, 2026';
const CONTACT_EMAIL = 'privacy@vowvisionsai.com';

const cookieTypes = [
  {
    name: 'Strictly Necessary',
    required: true,
    description: 'These cookies are essential for the website to function and cannot be switched off. They are usually set in response to actions you take such as logging in or filling in forms.',
    examples: [
      { name: 'auth_token', purpose: 'Keeps you logged in during your session', duration: 'Session' },
      { name: 'csrf_token', purpose: 'Protects against cross-site request forgery attacks', duration: 'Session' },
      { name: '__session', purpose: 'Manages authenticated user sessions via Supabase', duration: '7 days' },
    ]
  },
  {
    name: 'Functional',
    required: false,
    description: 'These cookies enable enhanced functionality and personalisation. They may be set by us or by third-party providers whose services we use.',
    examples: [
      { name: 'ui_preferences', purpose: 'Stores theme and display preferences', duration: '1 year' },
      { name: 'last_style', purpose: 'Remembers your last selected invitation style', duration: '30 days' },
    ]
  },
  {
    name: 'Analytics',
    required: false,
    description: 'These cookies allow us to count visits and traffic sources so we can measure and improve site performance. All data collected is anonymous and aggregated.',
    examples: [
      { name: '_cf_analytics', purpose: 'Cloudflare anonymised traffic analysis', duration: '30 days' },
    ]
  },
  {
    name: 'Third-Party Services',
    required: false,
    description: 'Some features of our Service rely on third-party providers that may set their own cookies. We only use services bound by data processing agreements.',
    examples: [
      { name: 'Supabase auth', purpose: 'Authentication and session management', duration: 'Session / 7 days' },
      { name: 'Cloudflare', purpose: 'CDN, security, and performance', duration: 'Up to 1 year' },
    ]
  }
];

export default function CookiePage() {
  return (
    <div className="min-h-screen bg-[#FBF8F3] text-[#211D1A]">
      <section className="border-b border-[#E8E0D6] bg-white pb-16 pt-32 md:pt-36">
        <div className="mx-auto max-w-4xl px-6">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.35em] text-[#8A7A6A]">Legal</p>
          <h1 className="font-serif text-4xl leading-tight text-[#211D1A] md:text-5xl">Cookie Policy</h1>
          <p className="mt-4 text-sm text-[#8A7A6A]">Last updated: {LAST_UPDATED}</p>
          <p className="mt-6 text-base leading-8 text-[#6C6258]">
            This Cookie Policy explains how VowVisionsAI uses cookies and similar technologies when you visit our website. It describes what they are, why we use them, and your choices regarding their use.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6 space-y-10">

          {/* What are cookies */}
          <div className="rounded-[28px] border border-[#E8DDD1] bg-white p-8">
            <h2 className="font-serif text-2xl text-[#211D1A]">What are cookies?</h2>
            <p className="mt-4 text-sm leading-8 text-[#5E564F]">
              Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work, remember your preferences, and provide information to the website owner. Cookies do not contain personal data by themselves but may be used in combination with other information to identify you.
            </p>
          </div>

          {/* Cookie types */}
          {cookieTypes.map((type) => (
            <div key={type.name} className="rounded-[28px] border border-[#E8DDD1] bg-white p-8">
              <div className="flex items-center gap-3">
                <h2 className="font-serif text-2xl text-[#211D1A]">{type.name}</h2>
                {type.required && (
                  <span className="rounded-full bg-[#F3E8D3] px-3 py-1 text-xs font-medium text-[#8E6B1C]">Always on</span>
                )}
              </div>
              <p className="mt-4 text-sm leading-8 text-[#5E564F]">{type.description}</p>
              <div className="mt-6 overflow-hidden rounded-[18px] border border-[#F0E8DC]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#F0E8DC] bg-[#FBF8F3]">
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.2em] text-[#8A7A6A]">Cookie</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.2em] text-[#8A7A6A]">Purpose</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-[0.2em] text-[#8A7A6A]">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {type.examples.map((ex, i) => (
                      <tr key={ex.name} className={i < type.examples.length - 1 ? 'border-b border-[#F0E8DC]' : ''}>
                        <td className="px-5 py-3 font-mono text-xs text-[#211D1A]">{ex.name}</td>
                        <td className="px-5 py-3 text-[#5E564F]">{ex.purpose}</td>
                        <td className="px-5 py-3 text-[#8A7A6A]">{ex.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Managing cookies */}
          <div className="rounded-[28px] border border-[#E8DDD1] bg-white p-8">
            <h2 className="font-serif text-2xl text-[#211D1A]">Managing your cookie preferences</h2>
            <p className="mt-4 text-sm leading-8 text-[#5E564F]">
              You can control and/or delete cookies as you wish. You can delete all cookies already on your device and you can set most browsers to prevent them from being placed.
            </p>
            <ul className="mt-4 space-y-2">
              {[
                'Chrome: Settings → Privacy and security → Cookies and other site data',
                'Safari: Preferences → Privacy → Manage Website Data',
                'Firefox: Options → Privacy & Security → Cookies and Site Data',
                'Edge: Settings → Cookies and site permissions → Manage and delete cookies',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm leading-7 text-[#5E564F]">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C6A14B]" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm leading-7 text-[#5E564F]">
              Note: blocking all cookies will affect the functionality of our Service, including keeping you logged in.
            </p>
          </div>

          {/* Changes */}
          <div className="rounded-[28px] border border-[#E8DDD1] bg-white p-8">
            <h2 className="font-serif text-2xl text-[#211D1A]">Changes to this policy</h2>
            <p className="mt-4 text-sm leading-8 text-[#5E564F]">
              We may update this Cookie Policy as our use of cookies evolves or as regulations change. We will update the "Last updated" date and, for significant changes, notify you through the Service.
            </p>
          </div>

          {/* Contact */}
          <div className="rounded-[28px] border border-[#E5D5BF] bg-[linear-gradient(180deg,#FFF9F1,#F8EEDF)] p-8 text-center">
            <p className="font-serif text-xl text-[#211D1A]">Questions about cookies?</p>
            <p className="mt-3 text-sm text-[#6C6258]">
              Email <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#C6A14B] underline underline-offset-2">{CONTACT_EMAIL}</a>
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
              <Link to="/privacy" className="text-[#6C6258] underline underline-offset-2 hover:text-[#211D1A]">Privacy Policy</Link>
              <span className="text-[#C6A14B]">·</span>
              <Link to="/terms" className="text-[#6C6258] underline underline-offset-2 hover:text-[#211D1A]">Terms of Service</Link>
              <span className="text-[#C6A14B]">·</span>
              <Link to="/help" className="text-[#6C6258] underline underline-offset-2 hover:text-[#211D1A]">Help Center</Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
